import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, LogOut, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");
      const res = await apiFetch("/api/user", {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error("Error deleting account");

      await logout();
      navigate("/login");
    } catch (err) {
      console.error(err);
      alert(t('profile.delete_error'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="page-container animate-fade-in" data-testid="profile-page">
      <div className="header">
        <h1 className="header-title">{t('profile.title')}</h1>
        <button
          type="button"
          className="profile-customize-btn"
          onClick={() => navigate('/customize')}
          aria-label={t('profile.customize_btn')}
          data-testid="profile-customize-btn"
        >
          <Settings size={20} />
        </button>
      </div>

      <div className="card" style={{ textAlign: 'center', padding: '32px' }}>
        {user?.picture ? (
          <img
            src={user.picture}
            alt={user.name}
            style={{ width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 16px' }}
          />
        ) : (
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '32px',
            fontWeight: '700',
            color: 'var(--primary-foreground)'
          }}>
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
        )}
        <h2 style={{ fontSize: '24px', marginBottom: '4px', color: 'var(--foreground)' }}>
          {user?.name || 'Usuario'}
        </h2>
        <p className="text-muted">{user?.email}</p>
      </div>

      <div className="card mt-4">
        <h3 style={{ fontSize: "16px", marginBottom: "16px", color: "var(--foreground)" }}>
          DragonFit
        </h3>
        <p style={{ fontSize: "18px", fontWeight: "700", textAlign: "center", marginBottom: "12px", letterSpacing: "0.5px" }}>
          {t('profile.tagline')}
        </p>
        <p style={{ fontSize: "13px", textAlign: "center", color: "var(--muted-foreground)", marginBottom: "20px" }}>
          {t('profile.credits')}{" "}
          <a href="https://instagram.com/mikel_lontso" target="_blank" rel="noopener noreferrer" style={{ fontWeight: "600" }}>
            @mikel_lontso
          </a>
        </p>
        <div style={{ textAlign: "center", fontSize: "14px" }}>
          <p style={{ marginBottom: "8px" }}>
            <a href="/privacy" style={{ fontWeight: "500" }}>{t('profile.privacy_link')}</a>
          </p>
          <p>
            <a href="https://dragonfit-trainingapp.netlify.app/" target="_blank" rel="noopener noreferrer" style={{ fontWeight: "500" }}>
              {t('profile.official_web')}
            </a>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-4">
        <button
          className="btn btn-danger w-full"
          onClick={handleLogout}
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          {t('profile.logout')}
        </button>
        <button
          className="btn btn-outline w-full"
          style={{ borderColor: "#ef4444", color: "#ef4444" }}
          onClick={() => setShowDeleteModal(true)}
        >
          {t('profile.delete_account')}
        </button>
      </div>

      {showDeleteModal && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999,
        }}>
          <div className="card" style={{ width: "90%", maxWidth: "400px", padding: "24px", textAlign: "center" }}>
            <h3 style={{ fontSize: "18px", marginBottom: "12px" }}>{t('profile.delete_confirm_title')}</h3>
            <p className="text-muted" style={{ marginBottom: "20px" }}>
              {t('profile.delete_confirm_desc')}
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              <button className="btn w-full" onClick={() => setShowDeleteModal(false)} disabled={isDeleting}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-danger w-full" onClick={handleDeleteAccount} disabled={isDeleting}>
                {isDeleting ? t('profile.deleting') : t('profile.delete_yes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;
